import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import * as yup from "yup";

import useYupValidation from "../../../../../../../hooks/useYupValidation";
import { Branch } from "../../../../../../../models/branch";
import { Contact } from "../../../../../../../models/contact";
import { Status } from "../../../../../../../util/enums";
import { ActionButtonProps } from "../../../../../../../components/Generic/ActionButtons";
import Modal, { StatusFieldProps } from "../../../../../../../components/Generic/Modal";
import UISteps, { Step } from "../../../../../../../components/Generic/UISteps";
import {
  useStepsAction,
  TypeOfButton,
} from "../../../../../../../components/Generic/UISteps/useActions";
import UITabMaterial, { PanelProps } from "../../../../../../../components/Generic/UITabMaterial";
import Contacts from "../../Warehousing/Warehouse/components/Contacts";
import General from "./General";
import useStyles from "./styles";
import { useBranchAction, Tabs } from "./useActions";

interface Props {
  branchToUpdate?: Branch;
  isOnViewMode?: boolean;
  hasErrors?: boolean;
  open: boolean;
  isLoading?: boolean;
  closeModal: () => void;
  submit: (branch: Branch) => void;
}

const BranchModal = ({
  branchToUpdate,
  open,
  hasErrors,
  isLoading = false,
  isOnViewMode = false,
  closeModal,
  submit,
}: Props) => {
  const classes = useStyles();
  const { t: translate } = useTranslation();
  const {
    branch,
    errors,
    options,
    currentStep,
    selectedLeftPanel,
    setSelectedLeftPanel,
    handleClearFields,
    handleUpdateBranch,
    handleValidate,
    nextStep,
    previousStep,
  } = useBranchAction();
  const { setButton, onCancel, onFinish } = useStepsAction();
  const { fieldHasError, getErrorByField } = useYupValidation(errors);

  const [hasFormBeenSubmitted, setHasFormBeenSubmitted] = useState<boolean>(false);

  const schema = yup.object().shape({
    code: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    name: yup
      .string()
      .required(translate("thisFieldIsRequired"))
      .typeError(translate("thisFieldIsRequired")),
    description: yup.string().nullable().typeError(translate("thisFieldIsRequired")),
    isValidateContact: yup.boolean(),
    contacts: yup.array().when("isValidateContact", {
      is: true,
      then: yup
        .array()
        .min(1, translate("youNeedToSelectAtLeastOneContact"))
        .required(translate("youNeedToSelectAtLeastOneContact")),
    }),
  });

  const generalTab: Step = {
    label: translate("branch"),
    dataCy: "generalStep",
    key: Tabs.BRANCH,
    hasError: !!(getErrorByField("name") || getErrorByField("code")),
    component: (
      <General
        isOnViewMode={isOnViewMode}
        branch={branch}
        getErrorByField={(value) => getErrorByField(value)}
        setBranch={(branch) => handleUpdateBranch(branch)}
        isFetching={isLoading}
      />
    ),
  };

  const contactTab: Step = {
    label: translate("contacts"),
    dataCy: "contactStep",
    key: Tabs.CONTACTS,
    hasError: fieldHasError("contacts"),
    component: (
      <Contacts
        contactsList={branch?.contacts || ([] as Contact[])}
        errorMessage={getErrorByField("contacts")}
        updateContactsOnWarehouse={(contacts: Contact[]) =>
          handleUpdateBranch({ ...branch, contacts })
        }
        isOnViewMode={isOnViewMode}
        isFetching={isLoading}
      />
    ),
  };

  const steps: Step[] = [generalTab, contactTab];

  const handleCloseModal = () => {
    handleClearFields();
    closeModal();
  };

  const handleSubmit = () => {
    if (!hasFormBeenSubmitted) setHasFormBeenSubmitted(true);

    onFinish(
      handleValidate(schema),
      !!branchToUpdate,
      options,
      steps,
      () => submit({ ...branch }),
      nextStep
    );
  };

  const handleSetButton = (button: string) =>
    setButton({
      type: button,
      isToUpdate: !!branchToUpdate,
      options,
      steps,
    });

  const handleSetModalTitle = (): string => {
    if (branchToUpdate) {
      if (isOnViewMode) return translate("viewBranch");

      return translate("editBranch");
    }

    return translate("createANewBranch");
  };

  const handleShowStatusField = (): boolean => currentStep === options[0] && !branchToUpdate;

  const statusFieldProps: StatusFieldProps = {
    label: translate("branchStatus"),
    value: branch?.status || Status.INACTIVE,
    dataCy: "selectStatus",
    placeholder: translate("selectAnOption"),
    handleChange: (value: string) => handleUpdateBranch({ ...branch, status: value }),
    disabled: isLoading,
  };

  const actionButtonProps: ActionButtonProps = {
    dataCySave: "submitButton",
    textSave: handleSetButton(TypeOfButton.SUBMIT).text,
    textCancel: handleSetButton(TypeOfButton.CANCEL).text,
    customColorSave: handleSetButton(TypeOfButton.SUBMIT).color,
    customColorCancel: handleSetButton(TypeOfButton.CANCEL).color,
    loading: isLoading,
    onSave: isOnViewMode ? undefined : () => handleSubmit(),
    onCancel: () => onCancel(options, !!branchToUpdate, handleCloseModal, previousStep),
  };

  const panels: PanelProps[] = steps.map((step) => ({
    menuItem: step.label,
    className: classes.modalTabBody,
    hasError: step.hasError,
    render: () => step.component,
  }));

  useEffect(() => {
    if (branchToUpdate) handleUpdateBranch(branchToUpdate);
  }, [branchToUpdate]);

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) handleCloseModal();
  }, [hasErrors]);

  useEffect(() => {
    if (hasFormBeenSubmitted) {
      handleValidate(schema);
    }
  }, [branch, hasFormBeenSubmitted]);

  return (
    <Modal
      data-cy="branchModal"
      open={open}
      handleClose={closeModal}
      title={handleSetModalTitle()}
      showActionButtons
      actionButtonProps={actionButtonProps}
      labelStatus={translate("status")}
      showStatusField={handleShowStatusField()}
      statusFieldProps={statusFieldProps}
      bodyClassName={!branchToUpdate ? classes.modalSection : ""}
      showModalBodyScroll={!branchToUpdate}
    >
      <div className={classes.paperBody}>
        {branchToUpdate ? (
          <UITabMaterial
            leftPanels={panels}
            isRenderingStatusField={true}
            selectedLeftPanel={selectedLeftPanel}
            onChangeLeftPanel={(_, value) => setSelectedLeftPanel(Number(value))}
            statusFieldProps={{ ...statusFieldProps, label: "" }}
          />
        ) : (
          <UISteps steps={steps} currentStep={currentStep} stepsOption={options} />
        )}
      </div>
    </Modal>
  );
};

export default BranchModal;
